import React, { useMemo, useState } from "react";
import { Tooltip } from "antd";
import { BellOutlined, DollarOutlined, EnvironmentOutlined, StarFilled, UserOutlined } from "@ant-design/icons";
import { DefaultRating } from "../../../assets/helpers";
import { formatDate } from "../../../shared/helpers";

const formatBudgetType = (budgetName = "") => {
  if (!budgetName) return "";
  if (budgetName.includes("Hour")) return budgetName.replace("Hour", "HR");
  if (budgetName.includes("Year")) return budgetName.replace("Year", "YR");
  return budgetName;
};

const getCandidateInitial = (candidate) =>
  candidate?.users?.Profiles?.FirstName?.trim()?.charAt(0)?.toUpperCase() || "C";

const getCandidateName = (candidate) => {
  const firstName = candidate?.users?.Profiles?.FirstName || "";
  const lastName = candidate?.users?.Profiles?.LastName || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || candidate?.users?.UserName || "Candidate name";
};

const getDesiredRate = (candidate) => {
  const clientRate = candidate?.ClientRate;
  if ([null, undefined, "", "null"].includes(clientRate)) {
    return "$Negotiable";
  }
  return `$${clientRate}`;
};

export default function ManageJobsInvitedCard({ candidate, onViewProfile, onPlaceBid }) {
  const [imageError, setImageError] = useState(false);

  const rawPictureUrl = candidate?.users?.Profiles?.PictureUrl;
  const profileImage = rawPictureUrl
    ? rawPictureUrl.startsWith('http')
      ? rawPictureUrl
      : `${process.env.REACT_APP_S3_URL}${rawPictureUrl.charAt(0) !== '/' ? '/' : ''}${rawPictureUrl}`
    : '';

  const ratingValue = useMemo(() => {
    const value = candidate?.users?.Profiles?.Rating;
    return value === 0 || value === null || value === undefined ? DefaultRating : Number(value);
  }, [candidate]);

  const reviewCount = candidate?.users?.Profiles?.Reviews;
  const experienceLabel =
    candidate?.users?.Profiles?.ExperienceLevelName?.YearsOfExperienceName || "N/A";
  const inviteMeta = candidate?.InvitedBy?.[0];
  const budgetType = formatBudgetType(candidate?.BudgetTypes?.BudgetTypeName);
  const topBadge = candidate?.uiBadge || candidate?.uiType;
  const topStatus = candidate?.uiStatus || "Invited";
  const amountLabel = candidate?.uiAmount || `${getDesiredRate(candidate)}${budgetType ? `/${budgetType}` : ""}`;
  const primaryActionLabel = candidate?.uiPrimaryAction || "Send Reminder";
  const primaryActionDisabled = candidate?.uiPrimaryActionDisabled ?? true;
  const cardTone = (topStatus || "").toLowerCase();

  return (
    <article className={`manage-jobs-invited-card manage-jobs-invited-card--${cardTone}`}>
      <div className="manage-jobs-invited-card__status-bar">
        <span className="manage-jobs-invited-card__status-dot" />
        <span className="manage-jobs-invited-card__status-text">{topStatus}</span>
      </div>

      <div className="manage-jobs-invited-card__content">
        <div className="manage-jobs-invited-card__head">
          <div className="manage-jobs-invited-card__identity">
            <div className="manage-jobs-invited-card__avatar">
              {profileImage && !imageError && !profileImage.includes("no-pic.png") ? (
                <img
                  src={profileImage}
                  alt={getCandidateName(candidate)}
                  onError={() => setImageError(true)}
                />
              ) : (
                <span>{getCandidateInitial(candidate)}</span>
              )}
            </div>

            <div className="manage-jobs-invited-card__identity-copy">
              <h3 className="manage-jobs-invited-card__name">{getCandidateName(candidate)}</h3>
              <div className="manage-jobs-invited-card__role">
                {candidate?.Title || "Profile title unavailable"}
              </div>
              <div className="manage-jobs-invited-card__meta-row">
                <span className="manage-jobs-invited-card__rating-cluster">
                  <StarFilled className="manage-jobs-invited-card__star-icon" />
                  <span className="manage-jobs-invited-card__rating-value">
                    {Number(ratingValue || DefaultRating).toFixed(1)}
                  </span>
                  <span className="manage-jobs-invited-card__rating-count">
                    ({reviewCount || 110})
                  </span>
                </span>
                <span className="manage-jobs-invited-card__meta-separator" />
                {topBadge ? (
                  <span className="manage-jobs-invited-card__chip">{topBadge}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="manage-jobs-invited-card__details">
          <div className="manage-jobs-invited-card__detail-row">
            <span className="manage-jobs-invited-card__detail-left">
              <DollarOutlined className="manage-jobs-invited-card__detail-icon" />
              <span className="manage-jobs-invited-card__detail-label">Estimated Amount:</span>
            </span>
            <span className="manage-jobs-invited-card__detail-value">{amountLabel}</span>
          </div>

          <div className="manage-jobs-invited-card__detail-row">
            <span className="manage-jobs-invited-card__detail-left">
              <UserOutlined className="manage-jobs-invited-card__detail-icon" />
              <span className="manage-jobs-invited-card__detail-label">Experience:</span>
            </span>
            <span className="manage-jobs-invited-card__detail-value">{experienceLabel}</span>
          </div>

          <div className="manage-jobs-invited-card__detail-row">
            <span className="manage-jobs-invited-card__detail-left">
              <EnvironmentOutlined className="manage-jobs-invited-card__detail-icon" />
              <span className="manage-jobs-invited-card__detail-label">Location</span>
            </span>
            <span className="manage-jobs-invited-card__detail-value">
              {candidate?.uiLocation || "Houston, TX"}
            </span>
          </div>
        </div>

        <div className="manage-jobs-invited-card__actions">
          <button
            type="button"
            className="manage-jobs-invited-card__secondary-btn"
            onClick={(event) => {
              event.stopPropagation();
              onViewProfile?.(candidate);
            }}
          >
            <UserOutlined />
            View Profile
          </button>

          <button
            type="button"
            className={`manage-jobs-invited-card__primary-btn ${
              primaryActionDisabled ? "manage-jobs-invited-card__primary-btn--disabled" : ""
            }`}
            onClick={(event) => {
              event.stopPropagation();
              if (!primaryActionDisabled) {
                onPlaceBid?.(candidate);
              }
            }}
            disabled={primaryActionDisabled}
          >
            <BellOutlined />
            {primaryActionLabel}
          </button>
        </div>
      </div>

      <Tooltip
        placement="topRight"
        title={
          <div className="manage-jobs-invited-card__tooltip">
            <div>{inviteMeta?.EmployerEmail || "No employer email available"}</div>
            <div>{inviteMeta?.InviteDate ? formatDate(inviteMeta.InviteDate) : "N/A"}</div>
          </div>
        }
      >
        <button
          type="button"
          className="manage-jobs-invited-card__info-btn"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <BellOutlined />
        </button>
      </Tooltip>
    </article>
  );
}
